import Konva from '../../../konva';

const CORNER_ROTATE_HANDLE_NAME = 'corner-rotate-handle';
const CORNER_ROTATE_BADGE_NAME = 'corner-rotate-badge';
const CORNER_ROTATE_ICON_NAME = 'corner-rotate-icon';
const CORNER_ROTATE_HIT_NAME = 'corner-rotate-hit';
const CORNER_ROTATE_SYNC_EVENT = 'cornerRotateSync';
/** Gap from top-right corner anchor to corner rotate icon (px). */
const CORNER_ROTATE_OFFSET = 16;

/** Circular refresh-style arrow (16×16 viewBox). */
const ROTATE_ICON_PATH =
  'M8 2.5a5.5 5.5 0 0 1 5.45 4.65L15 7l-2.75.25L12.95 4A5.5 5.5 0 1 0 8 13.5V15a7 7 0 1 1 0-14v1.5z';
const ROTATE_ICON_CENTER = 8;

const activeCornerRotations = new WeakMap();

const getCenter = (shape) => ({
  x:
    shape.x +
    (shape.width / 2) * Math.cos(shape.rotation) +
    (shape.height / 2) * Math.sin(-shape.rotation),
  y:
    shape.y +
    (shape.height / 2) * Math.cos(shape.rotation) +
    (shape.width / 2) * Math.sin(shape.rotation),
});

const rotateAroundPoint = (shape, angleRad, point) => {
  const x =
    point.x +
    (shape.x - point.x) * Math.cos(angleRad) -
    (shape.y - point.y) * Math.sin(angleRad);
  const y =
    point.y +
    (shape.x - point.x) * Math.sin(angleRad) +
    (shape.y - point.y) * Math.cos(angleRad);

  return {
    ...shape,
    rotation: shape.rotation + angleRad,
    x,
    y,
  };
};

const rotateAroundCenter = (shape, deltaRad) =>
  rotateAroundPoint(shape, deltaRad, getCenter(shape));

const getSnap = (snaps, newRotationRad, tol) => {
  let snapped = newRotationRad;
  snaps.forEach((snap) => {
    const angle = Konva.getAngle(snap);
    const absDiff = Math.abs(angle - newRotationRad) % (Math.PI * 2);
    const dif = Math.min(absDiff, Math.PI * 2 - absDiff);
    if (dif < tol) {
      snapped = angle;
    }
  });
  return snapped;
};

const getAbsoluteSelectionCenter = (transformer) => {
  const attrs = transformer._getNodeRect();
  return transformer.getAbsoluteTransform().point({
    x: attrs.width / 2,
    y: attrs.height / 2,
  });
};

const getCornerRotateHandleLayout = (transformer) => {
  const width = transformer.width();
  const height = transformer.height();
  const anchorSize = transformer.anchorSize();
  const padding = transformer.padding();
  const offset = CORNER_ROTATE_OFFSET;
  const verticalSign = height >= 0 ? 1 : -1;

  return {
    x: width + padding + offset,
    y: -offset * verticalSign - padding,
    offsetX: anchorSize / 2,
    offsetY: anchorSize / 2,
  };
};

const syncCornerRotateHandlePosition = (transformer, handle) => {
  if (!transformer || !handle) {
    return;
  }

  const layout = getCornerRotateHandleLayout(transformer);
  handle.position({ x: layout.x, y: layout.y });
  handle.offset({ x: layout.offsetX, y: layout.offsetY });
  handle.moveToTop();
};

const stopCornerRotateDrag = (transformer, evt) => {
  const dragState = activeCornerRotations.get(transformer);
  if (!dragState) {
    return;
  }

  window.removeEventListener('mousemove', dragState.onMove);
  window.removeEventListener('touchmove', dragState.onMove);
  window.removeEventListener('mouseup', dragState.onUp, true);
  window.removeEventListener('touchend', dragState.onUp, true);
  activeCornerRotations.delete(transformer);

  transformer._transforming = false;

  const node = transformer.getNode?.();
  transformer._fire?.('transformend', { evt, target: node });
  transformer.getNodes?.().forEach((target) => {
    target._fire?.('transformend', { evt, target });
  });

  const content = transformer.getStage()?.content;
  if (content) {
    content.style.cursor = '';
  }
};

const startCornerRotateDrag = (transformer, evt) => {
  if (
    !transformer?.rotateEnabled?.() ||
    transformer.isTransforming?.() ||
    transformer._transforming
  ) {
    return;
  }

  const stage = transformer.getStage();
  if (!stage) {
    return;
  }

  const nativeEvt = evt.evt || evt;
  stage.setPointersPositions(nativeEvt);
  const pointer = stage.getPointerPosition();
  if (!pointer) {
    return;
  }

  const absCenter = getAbsoluteSelectionCenter(transformer);
  const attrs = transformer._getNodeRect();
  const dragState = {
    startPointerAngle: Math.atan2(
      pointer.y - absCenter.y,
      pointer.x - absCenter.x,
    ),
    startRotation: attrs.rotation,
  };

  const onMove = (moveEvt) => {
    stage.setPointersPositions(moveEvt);
    const pp = stage.getPointerPosition();
    if (!pp) {
      return;
    }

    const center = getAbsoluteSelectionCenter(transformer);
    const pointerAngle = Math.atan2(pp.y - center.y, pp.x - center.x);
    let newRotation =
      dragState.startRotation + (pointerAngle - dragState.startPointerAngle);

    const tol = Konva.getAngle(transformer.rotationSnapTolerance?.() ?? 5);
    newRotation = getSnap(transformer.rotationSnaps?.() || [], newRotation, tol);

    const currentAttrs = transformer._getNodeRect();
    const diff = newRotation - currentAttrs.rotation;
    if (Math.abs(diff) < 1e-10) {
      return;
    }

    const shape = rotateAroundCenter(currentAttrs, diff);
    transformer._fitNodesInto(shape, { evt: moveEvt });

    const node = transformer.getNode?.();
    transformer._fire?.('transform', { evt: moveEvt, target: node });
    transformer.getNodes?.().forEach((target) => {
      target._fire?.('transform', { evt: moveEvt, target });
    });
  };

  const onUp = (upEvt) => {
    stopCornerRotateDrag(transformer, upEvt);
  };

  dragState.onMove = onMove;
  dragState.onUp = onUp;
  activeCornerRotations.set(transformer, dragState);

  transformer._transforming = true;

  const node = transformer.getNode?.();
  transformer._fire?.('transformstart', { evt: nativeEvt, target: node });
  transformer.getNodes?.().forEach((target) => {
    target._fire?.('transformstart', { evt: nativeEvt, target });
  });

  window.addEventListener('mousemove', onMove);
  window.addEventListener('touchmove', onMove, { passive: false });
  window.addEventListener('mouseup', onUp, true);
  window.addEventListener('touchend', onUp, true);
};

const applyCornerRotateHandleAppearance = (handle, appearance, anchorSize) => {
  const radius = (anchorSize || appearance.anchorSize || 14) / 2;
  const iconScale = (radius * 1.05) / ROTATE_ICON_CENTER;

  const badge = handle.findOne(`.${CORNER_ROTATE_BADGE_NAME}`);
  if (badge) {
    badge.fill(appearance.anchorFill);
    badge.stroke(appearance.anchorStroke);
    badge.radius(radius + 1);
  }

  const icon = handle.findOne(`.${CORNER_ROTATE_ICON_NAME}`);
  if (icon) {
    icon.fill(appearance.anchorStroke);
    icon.scale({ x: iconScale, y: iconScale });
    icon.offset({ x: ROTATE_ICON_CENTER, y: ROTATE_ICON_CENTER });
  }

  const hitArea = handle.findOne(`.${CORNER_ROTATE_HIT_NAME}`);
  if (hitArea) {
    hitArea.radius(radius + 4);
  }
};

const createCornerRotateHandle = (transformer, appearance) => {
  const anchorSize = transformer.anchorSize() || appearance.anchorSize || 14;
  const radius = anchorSize / 2;
  const iconScale = (radius * 1.05) / ROTATE_ICON_CENTER;

  const group = new Konva.Group({
    name: CORNER_ROTATE_HANDLE_NAME,
    listening: true,
  });

  const badge = new Konva.Circle({
    name: CORNER_ROTATE_BADGE_NAME,
    radius: radius + 1,
    fill: appearance.anchorFill,
    stroke: appearance.anchorStroke,
    strokeWidth: 2,
    listening: false,
  });

  const icon = new Konva.Path({
    name: CORNER_ROTATE_ICON_NAME,
    data: ROTATE_ICON_PATH,
    fill: appearance.anchorStroke,
    listening: false,
    scaleX: iconScale,
    scaleY: iconScale,
    offsetX: ROTATE_ICON_CENTER,
    offsetY: ROTATE_ICON_CENTER,
  });

  const hitArea = new Konva.Circle({
    name: CORNER_ROTATE_HIT_NAME,
    radius: radius + 4,
    fill: 'transparent',
    listening: true,
  });

  group.add(badge, icon, hitArea);

  group.on('mousedown.cornerRotate touchstart.cornerRotate', (evt) => {
    evt.cancelBubble = true;
    startCornerRotateDrag(transformer, evt);
  });

  group.on('mouseenter.cornerRotate', () => {
    const content = group.getStage()?.content;
    if (content) {
      content.style.cursor = 'crosshair';
    }
  });

  group.on('mouseout.cornerRotate', () => {
    const content = group.getStage()?.content;
    if (content && !transformer.isTransforming?.() && !activeCornerRotations.has(transformer)) {
      content.style.cursor = '';
    }
  });

  return group;
};

export const syncCornerRotateHandle = (transformer, options = {}) => {
  if (!transformer) {
    return undefined;
  }

  const {
    visible = false,
    anchorFill = '#FFFFFF',
    anchorStroke = '#3B82F6',
    anchorSize = 14,
  } = options;

  let handle = transformer.findOne(`.${CORNER_ROTATE_HANDLE_NAME}`);

  if (!visible) {
    stopCornerRotateDrag(transformer, {});
    handle?.visible(false);
    transformer.findOne(`.corner-rotate-connector`)?.destroy();
    transformer.off(`.${CORNER_ROTATE_SYNC_EVENT}`);
    return undefined;
  }

  transformer.findOne('.corner-rotate-connector')?.destroy();

  if (!handle) {
    handle = createCornerRotateHandle(transformer, {
      anchorFill,
      anchorStroke,
      anchorSize,
    });
    transformer.add(handle);

    const resync = () => syncCornerRotateHandlePosition(transformer, handle);
    transformer.on(`transform.${CORNER_ROTATE_SYNC_EVENT}`, resync);
    transformer.on(`dragmove.${CORNER_ROTATE_SYNC_EVENT}`, resync);
    transformer.on(`transformend.${CORNER_ROTATE_SYNC_EVENT}`, resync);
  } else {
    applyCornerRotateHandleAppearance(
      handle,
      {
        anchorFill,
        anchorStroke,
        anchorSize,
      },
      transformer.anchorSize() || anchorSize,
    );
    handle.visible(true);
  }

  syncCornerRotateHandlePosition(transformer, handle);
  transformer.getLayer()?.batchDraw();
  return handle;
};

export const detachCornerRotateHandle = (transformer) => {
  if (!transformer) {
    return;
  }
  stopCornerRotateDrag(transformer, {});
  transformer.off(`.${CORNER_ROTATE_SYNC_EVENT}`);
  transformer.findOne(`.${CORNER_ROTATE_HANDLE_NAME}`)?.destroy();
  transformer.findOne('.corner-rotate-connector')?.destroy();
};

export default syncCornerRotateHandle;
